import { mapObjIndexed, compose, values } from 'ramda';
import Mousetrap from 'mousetrap';
import shortcuts from './shortcuts';

const isEditable = (element: HTMLElement) =>
  element.tagName === 'INPUT' ||
  element.tagName === 'SELECT' ||
  element.tagName === 'TEXTAREA' ||
  ('contentEditable' in element && element.contentEditable === 'true');

// The Conversational AI web component keeps its chat input inside a shadow root, so
// keyboard events retarget to the <conversational-ai> host and isEditable() can't see
// the real input. Treat the whole CA panel as an editable context so single-key app
// shortcuts don't fire while the user is typing there.
const isConversationalAi = (element: HTMLElement) =>
  element.tagName === 'CONVERSATIONAL-AI' || Boolean(element.closest?.('.conversational-ai'));

const isSimpleShortcut = (combination: string) => {
  if (combination.split(' ').length > 1) {
    // sequence
    return true;
  }

  const combinationSanitized = combination.replace(/\+{2}/g, '+plus');

  if (combinationSanitized.split('+').length === 1) {
    // single key shortcut
    return true;
  }

  // shortcut with modifier key
  return false;
};

Mousetrap.prototype.stopCallback = function stopCallback(
  _event: KeyboardEvent,
  element: HTMLElement | HTMLInputElement,
  combo: string
) {
  // cancel works everywhere
  if (combo === shortcuts.cancel) {
    return false;
  }

  // if the element has the class "mousetrap" then no need to stop
  if (` ${element.className} `.indexOf(' mousetrap ') > -1) {
    return false;
  }

  // stop for input, select, textarea, and the Conversational AI component,
  // but allow combos with modifier keys (so copy/paste etc. still work)
  return (isEditable(element) || isConversationalAi(element)) && isSimpleShortcut(combo);
};

/**
 * Shortcut mappings.
 */
export { default as shortcuts } from './shortcuts';

const shortcutMap: Record<string, Array<(event: KeyboardEvent) => void>> = {};

/**
 * @returns {function} Function that unbinds the list of shortcuts.
 */
const createUnbindShortcuts =
  (list: Array<[string, (event: KeyboardEvent) => void]>): (() => void) =>
  () => {
    // unbind the currently active shortcut
    Mousetrap.unbind(list.flatMap(([key]) => key));

    list.forEach(([key, action]) => {
      const storedActions = shortcutMap[key];

      // remove the shortcut action by identity, to prevent race conditions
      // with other shortcuts binding for the same key
      shortcutMap[key] = storedActions.filter((storedAction) => storedAction !== action);

      const updatedShortcuts = shortcutMap[key];

      if (updatedShortcuts.length) {
        // bind the latest shortcut if available
        Mousetrap.bind(key, updatedShortcuts[updatedShortcuts.length - 1]);
      }
    });
  };

/**
 * Bind functions to shortcuts.
 *
 * @param {Object} mapping Mapping of shortcuts to functions.
 * @returns {function} Function to unbind shortcuts.
 * @example
 * const unbindShortcuts = bindShortcuts({
 *    'go:back': () => this.$router.go(-1),
 *    'go:forward': () => this.$router.go(1),
 *  });
 */
export const bindShortcuts = compose(
  createUnbindShortcuts,
  values,
  mapObjIndexed((action: (event: KeyboardEvent) => void, key: string) => {
    const shortcut = shortcuts[key];
    Mousetrap.bind(shortcut, action);

    // Mousetrap by default only allows one action per shortcut, but we need to stack actions.
    // We keep a list of actions per shortcut, and if the latest gets unbound,
    // we re-bind the previous one
    if (shortcutMap[shortcut]) {
      shortcutMap[shortcut].push(action);
    } else {
      shortcutMap[shortcut] = [action];
    }

    return [shortcut, action];
  })
);

/**
 *
 * for removing shortcuts from the map
const tf = fromPairs(xprod(p, [slice(0, -1)]));
evolve(tf, blub)

The map does not support array shortcuts yet.
*/
